import { useCallback } from 'react';

import { useRehabStates } from 'context/rehabStates';
import { useTranslation } from 'context/translations';
import { PERMISSIONS } from 'models/Profile';
import { RehabStateApiName, RehabStates } from 'models/RehabState';

type Rules = {
  [State in RehabStateApiName]: {
    accepts: RehabStateApiName[];
    rejects: {
      states: RehabStateApiName[];
      message: string;
    }[];
    permission: {
      permission?: string;
      message?: string;
    };
  };
};

export const useTransitionRules = (): Rules => {
  const rehabStates = useRehabStates();
  const { translate } = useTranslation();

  const getRehabStateDisplayName = useCallback(
    (apiName: RehabStateApiName, fallback: string) => {
      return translate(rehabStates.find((state) => state.apiName === apiName)?.state || fallback);
    },
    [rehabStates, translate]
  );

  return {
    [RehabStateApiName.Queue]: {
      accepts: [RehabStateApiName.Queue, RehabStateApiName.InTreatment],
      rejects: [
        {
          states: [RehabStateApiName.Discharged],
          message: `Patient must be moved to ${getRehabStateDisplayName(RehabStateApiName.InTreatment, RehabStates.InTreatment.state)} or removed from ${getRehabStateDisplayName(RehabStateApiName.Queue, RehabStates.Queue.state)} in Olio. Contact support@olio.health for assistance.`,
        },
      ],
      permission: {},
    },
    [RehabStateApiName.InTreatment]: {
      accepts: [RehabStateApiName.InTreatment, RehabStateApiName.Discharged],
      rejects: [
        {
          states: [RehabStateApiName.Queue],
          message: `Patients cannot be moved back to ${getRehabStateDisplayName(RehabStateApiName.Queue, RehabStates.Queue.state)}. Please contact support@olio.health for assistance.`,
        },
      ],
      permission: {
        permission: PERMISSIONS.rehabStateAdmissionCreate,
        message: `You do not have permission to move a patient to ${getRehabStateDisplayName(RehabStateApiName.InTreatment, RehabStates.InTreatment.state)}. Contact support@olio.health for assistance.`,
      },
    },
    [RehabStateApiName.Discharged]: {
      accepts: [RehabStateApiName.Discharged],
      rejects: [
        {
          states: [RehabStateApiName.Queue, RehabStateApiName.InTreatment],
          message: `Patients in ${getRehabStateDisplayName(RehabStateApiName.Discharged, RehabStates.Discharged.state)} state cannot be moved back to ${getRehabStateDisplayName(RehabStateApiName.Queue, RehabStates.Queue.state)} or ${getRehabStateDisplayName(RehabStateApiName.InTreatment, RehabStates.InTreatment.state)}. If you need assistance, please contact support@olio.health`,
        },
      ],
      permission: {
        permission: PERMISSIONS.rehabStateDischargeCreate,
        message: `You currently do not have permission to move patients to ${getRehabStateDisplayName(RehabStateApiName.Discharged, RehabStates.Discharged.state)} in Olio. Contact support@olio.health with questions.`,
      },
    },
  };
};
